import { Mesh } from 'three/src/objects/Mesh';
import { FillAnchor } from './stopeGeometry';
import { StopeFillPour } from './pour';
import {
  runStopeFillWalkJob,
  StopeFillWalkOutcome,
  StopeFillWalkResponse,
  toWalkOutcome,
  toWalkRequest
} from './stopeFillWalkJob';

export interface CancellableWalk<T extends StopeFillPour> {
  result: Promise<StopeFillWalkOutcome<T>>;
  cancel: () => void;
}

const startWorker = (): Worker | null => {
  try {
    return new Worker(new URL('./stopeFillWalk.worker.ts', import.meta.url), { type: 'module' });
  } catch {
    return null;
  }
};

export const walkStopeFillOffThread = <T extends StopeFillPour>(
  stope: Mesh,
  orderedPours: T[],
  defaultAnchor: FillAnchor,
): CancellableWalk<T> => {
  const request = toWalkRequest(stope, orderedPours, defaultAnchor);
  const inThread = () => toWalkOutcome(runStopeFillWalkJob(request), stope, orderedPours);
  const worker = startWorker();

  if (!worker) {
    return { result: Promise.resolve().then(inThread), cancel: () => { } };
  }

  const result = new Promise<StopeFillWalkOutcome<T>>((resolve, reject) => {
    const settle = (outcome: () => StopeFillWalkOutcome<T>) => {
      worker.terminate();
      try {
        resolve(outcome());
      } catch (error) {
        reject(error);
      }
    };

    worker.onmessage = ({ data }: MessageEvent<StopeFillWalkResponse>) => settle(() => toWalkOutcome(data, stope, orderedPours));
    worker.onerror = (event) => {
      event.preventDefault();
      settle(inThread);
    };
  });

  worker.postMessage(request);

  return { result, cancel: () => worker.terminate() };
};
